import { c as _c } from "react/compiler-runtime";
// «Удалить аккаунт» в «Личных данных» (App Store 5.1.1(v), Google Play,
// 152-ФЗ). Макет профиля этого действия не рисует - оно стоит вторичной
// ссылкой в самом низу, под «Вход через сервисы», и открывает окно в языке
// «Смены номера»: последствия → что мешает (если есть) → код из SMS → выход.
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@osmix/ui/dialog";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { toast } from "sonner";
import { api, type AccountDeletionBlocker } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/text-field";
import { setBuyerAuthToken } from "@/api/token";
import { useProfile } from "@/store";
const OVERLAY = "bg-black/20 backdrop-blur-[2px]";
export function DeleteAccount() {
  const $ = _c(10);
  const {
    t
  } = useTranslation();
  const [open, setOpen] = useState(false);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => setOpen(true);
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== t) {
    t1 = t("accountDeletion.link");
    $[1] = t;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t1) {
    t2 = <button type="button" data-testid="delete-account" onClick={t0} className="mt-10 block rounded-sm text-[14px] leading-[18px] text-[#1F1F1F]/48 outline-none hover:text-[#E1574F] hover:underline focus-visible:ring-2 focus-visible:ring-[#1F1F1F]/40">{t1}</button>;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== open) {
    t3 = <DeleteAccountDialog open={open} onOpenChange={setOpen} />;
    $[5] = open;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t2 || $[8] !== t3) {
    t4 = <>{t2}{t3}</>;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}
function DeleteAccountDialog({
  open,
  onOpenChange
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const {
    t
  } = useTranslation();
  const navigate = useNavigate();
  const signOut = useProfile(s => s.signOut);
  const [blockers, setBlockers] = useState<AccountDeletionBlocker[] | null>(null);
  const [sessionId, setSessionId] = useState("");
  const [masked, setMasked] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setBlockers(null);
    setSessionId("");
    setCode("");
    api.accountDeletion.check().then(r => setBlockers(r.blockers)).catch(() => setBlockers([]));
  }, [open]);
  const getCode = async () => {
    setBusy(true);
    try {
      const s_0 = await api.accountDeletion.start();
      setSessionId(s_0.sessionId);
      setMasked(s_0.maskedTarget);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t("accountDeletion.failed"));
      void api.accountDeletion.check().then(r_0 => setBlockers(r_0.blockers)).catch(() => undefined);
    } finally {
      setBusy(false);
    }
  };
  const confirm = async () => {
    setBusy(true);
    try {
      const r_1 = await api.accountDeletion.confirm(sessionId, code);
      if (!r_1.deleted) {
        if (r_1.attemptsLeft > 0) toast.error(t("accountDeletion.wrongCode", {
          count: r_1.attemptsLeft
        }));else {
          toast.error(t("accountDeletion.expired"));
          setSessionId("");
        }
        setCode("");
        return;
      }
      onOpenChange(false);
      // Сеансы на сервере уже погашены: токен забываем сразу (выход на
      // сервере звать незачем - он ответил бы 401), чистим устройство и
      // уходим на главную.
      setBuyerAuthToken(null);
      useProfile.setState({
        token: null
      });
      await signOut();
      toast.success(t("accountDeletion.done"));
      navigate("/", {
        replace: true
      });
    } catch (e_0) {
      toast.error(e_0 instanceof Error ? e_0.message : t("accountDeletion.failed"));
    } finally {
      setBusy(false);
    }
  };
  const blocked = (blockers?.length ?? 0) > 0;
  return <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent overlayClassName={OVERLAY} className="max-h-[90dvh] gap-3 overflow-y-auto rounded-[20px] border-0 bg-white p-4 pt-[18px] sm:max-w-[420px]" data-testid="delete-account-dialog">
        <DialogTitle className="ds-h3 text-ds-ink">{t("accountDeletion.title")}</DialogTitle>
        <DialogDescription className="text-[14px] leading-[18px] font-medium text-[#1F1F1F]">
          {t("accountDeletion.lead")}
        </DialogDescription>
        <ul className="flex list-disc flex-col gap-1.5 pl-5 text-[14px] leading-[18px] text-[#1F1F1F]/72">
          <li>{t("accountDeletion.removed")}</li>
          <li>{t("accountDeletion.kept")}</li>
          <li>{t("accountDeletion.again")}</li>
        </ul>
        {blockers === null ? <p className="text-[14px] text-[#1F1F1F]/56">{t("accountDeletion.checking")}</p> : blocked ? <div className="rounded-[12px] bg-[#E1574F]/8 p-3 text-[14px] leading-[18px] text-[#1F1F1F]" data-testid="delete-account-blockers">
            <p className="font-semibold">{t("accountDeletion.blocked")}</p>
            <ul className="mt-1 flex list-disc flex-col gap-1 pl-5">
              {blockers.map(b => <li key={b.kind}>
                  {b.message}
                  {b.orderNumbers?.length ? ` (${b.orderNumbers.join(", ")})` : ""}
                </li>)}
            </ul>
          </div> : !sessionId ? <Button block disabled={busy} onClick={() => void getCode()} data-testid="delete-account-get-code">
            {t("accountDeletion.getCode")}
          </Button> : <>
            <p className="text-[14px] leading-[18px] text-[#1F1F1F]">
              {t("accountDeletion.codeSent", {
            phone: masked
          })}
            </p>
            <Input size="lg" value={code} onChange={e_1 => setCode(e_1.target.value.replace(/\D/g, "").slice(0, 6))} placeholder={t("accountDeletion.codeLabel")} aria-label={t("accountDeletion.codeLabel")} inputMode="numeric" autoComplete="one-time-code" autoFocus data-testid="delete-account-code" />
            <Button block disabled={busy || code.length < 4} onClick={() => void confirm()} variant="danger" data-testid="delete-account-confirm">
              {t("accountDeletion.confirm")}
            </Button>
          </>}
        <Button block variant="ghost" onClick={() => onOpenChange(false)}>
          {t("accountDeletion.cancel")}
        </Button>
      </DialogContent>
    </Dialog>;
}